import { Image } from "antd";
import { useTranslation } from "react-i18next";
import CommonIcons from "@base/assets/icons";
import { imageSpecialization } from "@base/constants";
import { hasText } from "@base/modules/doctor/doctorProfile";
import { IDoctor } from "@base/modules/doctor/interface/doctor.interface";
import CollapsibleText from "@components/CommonStyles/CollapsibleText";
import SectionCard from "./SectionCard";

interface IProps {
  doctor: IDoctor;
}

/**
 * The read-only part of a doctor's profile: work history, introduction, specialty and achievements.
 * Each block is left out entirely when the doctor has nothing for it.
 */
export default function DoctorProfileSections({ doctor }: IProps) {
  //! state
  const { t } = useTranslation();
  const specialization = doctor.specialization;
  // The label is `description` - `specialization_name` is a short internal code.
  const specializationLabel = specialization?.description;
  const SpecializationIcon = imageSpecialization.find((item) => item.key === specialization?.specialization_code)?.value ?? CommonIcons.No;
  const degrees = (doctor.degrees ?? []).filter((degree) => !!degree.link);

  //! render
  return (
    <>
      {hasText(doctor.experience_details) ? (
        <SectionCard title={t("doctorDetail.experience")}>
          <CollapsibleText text={doctor.experience_details} />
        </SectionCard>
      ) : null}

      {hasText(doctor.summary) ? (
        <SectionCard title={t("doctorDetail.introduction")}>
          <CollapsibleText text={doctor.summary} />
        </SectionCard>
      ) : null}

      {hasText(specializationLabel) ? (
        <SectionCard title={t("doctorDetail.specialization")}>
          <div className="flex items-center gap-4 w-fit max-w-full pr-6 p-2 rounded-xl bg-common-background">
            {/* The shared specialty icons are drawn at 100px for the specialty list. */}
            <div className="flex-none w-16 h-16 [&_svg]:w-full [&_svg]:h-full [&>div]:w-full [&>div]:h-full">
              <SpecializationIcon />
            </div>
            <span className="text-sm lg:text-base font-medium font-['Inter'] text-common-maintext">{specializationLabel}</span>
          </div>
        </SectionCard>
      ) : null}

      {degrees.length ? (
        <SectionCard title={t("doctorDetail.achievements")}>
          <Image.PreviewGroup>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {degrees.map((degree) => (
                <Image key={degree.link} src={degree.link} alt={degree.object_name} className="rounded-lg object-cover" />
              ))}
            </div>
          </Image.PreviewGroup>
        </SectionCard>
      ) : null}
    </>
  );
}
